/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import isObject from 'lodash.isobject';
import get, {getEnvironment} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {AdmRootFolder, getFolderColorByOid, RootFolder as IonRootFolder} from '@ion/core/src/services/SisenseUtils';
import {GenericObject} from '@ion/core/src/types/entities';
import {getHistoryColors} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {AdmSideNavTabs, GetCurrentDashboardsParams, SideNavTabs as IonSideNavTabs} from './types';

const KEY_MAP: Record<string, string> = {
  id : 'oid'
};

/**
 * Structure of a dashboard category object returned by formatDashboards.
 */
export interface DashboardCategory {
  name: string;
  dashboards: any[];
  source: any | null;
  color?: string;
  folders?: DashboardCategory[];
};

export interface FormatDashboardsParams {
  search: GenericObject;
  isAdmReports?: boolean;
  tab: AdmSideNavTabs | IonSideNavTabs;
  filterByDate?: boolean;
  returnOnlyUncategorized?: boolean;
  generateColor?: boolean;
  features?: GenericObject[];
};

/**
 * Formats raw dashboard data into categories based on the selected tab (Basic or Advanced).
 *
 * @param search       - Raw data from the backend containing folders and dashboards.
 * @param tab          - Whether the user is viewing Basic or Advanced tab.
 * @param filterByDate - Whether to include only recently accessed dashboards.
 * @returns An array of categorized dashboards.
 */
export const formatDashboards = ({
  search,
  tab,
  features,
  filterByDate,
  returnOnlyUncategorized,
  isAdmReports = false,
  generateColor = false
}: FormatDashboardsParams): {
  dashboardCategories: DashboardCategory[],
  allDashOIDs: string[]
} => {
  let colorStartIdx = 0;
  const allDashOIDs = [];

  const RootFolder  = isAdmReports ? AdmRootFolder : IonRootFolder;
  const SideNavTabs = isAdmReports ? AdmSideNavTabs : IonSideNavTabs;

  const isPermitted = (oid) => {
    return features?.some(feature => {
      return feature?.description?.includes(String(oid));
    });
  };

  const processFolder = (folder: any): DashboardCategory | null => {
    if (!isAdmReports && !isPermitted(folder.oid)) return null;

    const categoryName = folder.name?.trim();

    let filteredDashboards = folder.dashboards?.filter(
      ({title, oid}) => {
        allDashOIDs.push(oid as unknown as never);
        return (isAdmReports || isPermitted(oid)) &&!title?.toLowerCase().includes('_drill');
      }) ?? [];

    if (filterByDate) {
      filteredDashboards = filteredDashboards.filter(({lastOpened}: any) => !!lastOpened);
    }

    const processedSubFolders = folder.folders?.map(processFolder).filter(Boolean) ?? [];

    const isEmptyAfterFilter = filteredDashboards.length === 0 && processedSubFolders.length === 0;
    if (isEmptyAfterFilter) {
      return null;
    }
   
    return {
      name       : categoryName ?? '',
      folders    : processedSubFolders,
      source     : folder,
      dashboards : filteredDashboards.sort((a, b) => a.title.localeCompare(b.title))
        .map(dash => ({...dash, tab, categoryName}))
    };
  };

  let dashboardCategories: DashboardCategory[] = [];

  if (!returnOnlyUncategorized) {
    const folderName       = tab === SideNavTabs.Basic ? RootFolder.Basic : IonRootFolder.Advanced;
    const firstLevelFolder = search?.folders?.find(folder => folder.name === folderName);

    if (firstLevelFolder?.folders) {
      dashboardCategories = firstLevelFolder.folders
        .map(processFolder)
        .filter(Boolean) as DashboardCategory[];
    }
  }

  if (returnOnlyUncategorized) {
    const uncategorized = (search?.dashboards?.filter(
      ({oid, title, lastOpened}) => {
        allDashOIDs.push(oid as any as never);
        return (
          !title?.toLowerCase().includes('_drill') &&
          (!filterByDate || !!lastOpened)
        );
      }) ?? []);

    if (uncategorized.length) {
      const categoryName = translate('sisense:analytics.folderNames.myReports');
      dashboardCategories.push({
        name       : categoryName,
        folders    : [],
        source     : null,
        dashboards : uncategorized.sort((a, b) => a.title.localeCompare(b.title)).map(dash => ({
          ...dash, tab, categoryName
        }))
      });
    }
  }

  const sortCategories = (categories: DashboardCategory[]) => {
    categories.sort((a, b) => {
      if (a.name === translate('sisense:analytics.folderNames.myReports')) return 1;
      if (b.name === translate('sisense:analytics.folderNames.myReports')) return -1;
      return a.name.localeCompare(b.name);
    });
    categories.forEach(cat => sortCategories(cat.folders ?? []));
  };
  sortCategories(dashboardCategories);

  if (generateColor) {
    const colors = Object.values(getHistoryColors());
    const colorCount = colors.length;

    const assignColors = (categories: DashboardCategory[]) => {
      categories.forEach((cat) => {
        cat.color = colors[colorStartIdx % colorCount];
        colorStartIdx++;
        assignColors(cat.folders ?? []);
      });
    };
    assignColors(dashboardCategories);
  }

  return {dashboardCategories, allDashOIDs};
};

/**
 * Returns a flat list of dashboards based on selected category, 
 * search value, and sorting preference.
 *
 * @param params - Object containing categories, selectedCategory,
 *                 searchValue, categoryFilter, and sortByDate.
 * @returns A filtered and sorted array of dashboards.
 */
export const getCurrentDashboards = ({
  categories,
  selectedCategory,
  searchValue,
  sortByDate,
  categoryFilter
}: GetCurrentDashboardsParams): any[] => {
  const categoryFilterValues = categoryFilter?.map(({value}) => value) ?? [];
  const collectDashboards = (
    categories: any[],
    parentColor?: string,
    parentSource?: any
  ): any[] => {
    const result: any[] = [];

    categories.forEach(({
      dashboards,
      source,
      color,
      folders
    }) => {
      result.push(
        ...dashboards.map(dashboard => ({
          ...dashboard,
          category : source ?? parentSource,
          color    : color ?? parentColor
        }))
      );

      if (folders?.length) {
        result.push(
          ...collectDashboards(folders, color ?? parentColor, source ?? parentSource)
        );
      }
    });

    return result;
  };

  let dashboards: any[] = [];

  if (selectedCategory) {
    const findCategoryRecursive = (categories: any[]): any | null => {
      for (const cat of categories) {
        if (cat.name === selectedCategory) return cat;
        if (cat.folders?.length) {
          const found = findCategoryRecursive(cat.folders);
          if (found) return found;
        }
      }
      return null;
    };

    const selectedCat = findCategoryRecursive(categories);
    if (selectedCat) {
      dashboards = collectDashboards([selectedCat]);
    }
  } else {
    dashboards = collectDashboards(categories);
  }

  dashboards = dashboards.filter(({oid, title}) =>
    translateFolderOrDashboard(oid, title, 'dashboard')
      ?.toLowerCase()
      .includes(searchValue.toLowerCase())
  );

  dashboards = dashboards.filter(({category}) => categoryFilterValues.includes(category?.oid));

  dashboards.sort((a, b) => {
    if (sortByDate) {
      const aTime = new Date(a.lastOpened ?? 0).getTime();
      const bTime = new Date(b.lastOpened ?? 0).getTime();
      return bTime - aTime;
    }
    return a.title.localeCompare(b.title);
  });

  return dashboards;
};

/**
 * Finds the hierarchical folder path leading to a given dashboard OID
 * within the Sisense search structure.
 *
 * This function recursively traverses all folders and subfolders to locate
 * the target dashboard and returns an array of folder names representing the
 * path. If the dashboard is located at the root (uncategorized), it will
 * return `["Uncategorized"]`. If the dashboard cannot be found, it will
 * return an empty array.
 *
 * @param search       - The Sisense search object containing folders and dashboards.
 * @param dashboardOid - The unique identifier (OID) of the dashboard to find.
 * @returns            - An array of folder names representing the path to the dashboard,
 *                       or `["Uncategorized"]` if it is not in a folder, or `[]` 
 *                       if not found.
 *
 * ```typescript
 * import {findDashboardPath} from '@ion/applications/src/pages/analytics/utils';
 *
 * // Example search object with folders
 * const path = findDashboardPath(search, 'dashboard-1234');
 * // => ["Common Reports", "Q1 Dashboards"]
 *
 * const uncategorized = findDashboardPath(search, 'dashboard-5678');
 * // => ["Uncategorized"]
 *
 * const notFound = findDashboardPath(search, 'non-existent-oid');
 * // => []
 * ```
 */
export const findDashboardPath = (
  search: GenericObject,
  dashboardOid: string
): string[] => {
  const traverse = (folders, currentPath: string[]): string[] | null => {
    for (const folder of folders) {
      const folderName = folder.name?.trim() ?? '';
      if (folder.dashboards?.some(d => d.oid === dashboardOid)) {
        return [...currentPath, folderName];
      }
      if (folder.folders?.length) {
        const result = traverse(folder.folders, [...currentPath, folderName]);
        if (result) return result;
      }
    }
    return null;
  };
  if (search.folders) {
    const result = traverse(search.folders, []);
    if (result) return result;
  }
  if (search.dashboards?.some(d => d.oid === dashboardOid)) {
    return ['Uncategorized'];
  }
  return [];
};

/**
 * Returns the themed color value for a given folder's OID using the DLS color system.
 *
 * This function internally maps the folder OID to a predefined DLS color name using
 * `getFolderColorByOid`, and then resolves the actual color value using `dlsColor`.
 *
 * @param folderName - The unique folder OID (as a string).
 * @returns The resolved color string from the DLS theme (e.g., `#02ABCA`).
 *
 * ```typescript
 * const color = getFolderColor('64a79c11d6d989003643370d');
 * // Returns the actual color string (e.g. '#02ABCA') based on theme
 * ```
 */
export const getFolderColor = (folderName: string) => dlsColor(getFolderColorByOid(folderName));

/**
 * Converts a name to camelCase and translates using a constructed i18n key.
 * Falls back to the original name if no translation is found.
 *
 * @param name - Full name of the folder or dashboard (e.g. "Trip, Stop & Location Reports")
 * @param type - Either 'folder' or 'dashboard'
 * @returns Translated string or original name if translation not found
 *
 * @example
 * translateFolderOrDashboard('45678909876545ghjfrtyhj89', 'Trip, Stop & Location Reports', 'dashboard');
 * // → translate('sisense:analytics.dashboardNames.tripStopLocationReports')
 */
export const translateFolderOrDashboard = (
  oid: string,
  name: string,
  type: 'folder' | 'dashboard'
): string => {
  if (!name && type === 'folder') return translate('sisense:analytics.folderNames.myReports');

  const serviceEnv = getEnvironment(get('ctcURL'));
  const entries    = translate(`sisense:analytics.${type}Names`, {returnObjects: true});
  const names      = type ==='folder' ? (entries?.[serviceEnv] ?? entries.qa) : entries;

  return names[oid] ?? name;
};

/**
 * Converts a list of category objects into filter options for the report category
 * filter dropdown. Each category is mapped to an object containing a translated
 * label and a unique `value` derived from the category's `source.oid`. These options
 * are used by the dropdown component in `ReportsFilter`.
 *
 * ```typescript
 * const categories = [
 *   { name: 'Finance', source: { oid: '123' } },
 *   { name: 'Marketing', source: { oid: '456' } }
 * ];
 *
 * const options = getReportFilterOptions(categories);
 * // Result:
 * // [
 * //   { label: 'Finance', value: '123' },
 * //   { label: 'Marketing', value: '456' }
 * // ]
 * ```
 * @param categories - An array of category objects, each containing at least
 *                    `name` and `source.oid`.
 * 
 * @returns A list of dropdown-compatible option objects,
 *          or `undefined` if `categories` is falsy.
 *
 */
export const getReportFilterOptions = (categories: GenericObject[] = []): { label: string; value: string }[] => {
  const options: {label: string; value: string}[] = [];
  const traverse = (categories: GenericObject[]) => {
    categories.forEach(({name, source, folders}) => {
      options.push({
        label : translateFolderOrDashboard(source?.oid, name, 'folder'),
        value : source?.oid
      });

      if (folders?.length) {
        traverse(folders);
      }
    });
  };
  traverse(categories);
  return options;
};

/**
 * Recursively traverses a tree-like data structure and renames object keys
 * based on a predefined key mapping. This function is designed to work with
 * deeply nested objects and arrays, such as folder–dashboard hierarchies.
 *
 * Currently, the following key transformation is applied:
 * - `id` → `oid`
 *
 * All other keys and values are preserved as-is. The function safely handles:
 * - Plain objects
 * - Arrays of objects
 * - Deeply nested structures
 * - Primitive values (`string`, `number`, `boolean`, `null`, `undefined`)
 *
 * ```ts
 * const input = {
 *   id: 'root',
 *   folders: [
 *     {
 *       id: 'folder-1',
 *       dashboards: [
 *         { id: 'dash-1', title: 'Dashboard A' }
 *       ]
 *     }
 *   ]
 * };
 *
 * const result = transformTree(input);
 *
 * // Result:
 * // {
 * //   oid: 'root',
 * //   folders: [
 * //     {
 * //       oid: 'folder-1',
 * //       dashboards: [
 * //         { oid: 'dash-1', title: 'Dashboard A' }
 * //       ]
 * //     }
 * //   ]
 * // }
 * ```
 *
 * @param data - The input value to transform. Can be a plain object, an array,
 *               or any primitive value. Arrays and objects are traversed
 *               recursively.
 *
 * @returns A new data structure with mapped keys applied recursively.
 *          Primitive values are returned unchanged.
 */
export const transformTree = (data: unknown): any => {
  if (Array.isArray(data)) {
    return data.map(transformTree);
  }

  if (data && isObject(data)) {
    const result: any = {};

    for (const [key, value] of Object.entries(data)) {
      const newKey = KEY_MAP[key] ?? key;
      result[newKey] = transformTree(value);
    }

    return result;
  }

  return data;
};
